import { Link, useParams } from 'react-router';
import { LandingPagesNavbar, LandingPagesFooter } from '@podus/ui';
import { getPost, type Block, type Post } from '@/blog/posts';
import { useSeo } from '@/blog/useSeo';
import NotFound from '@/pages/NotFound';

const SITE = 'https://podus.org';

function renderBlock(block: Block, i: number) {
    switch (block.type) {
        case 'h2':
            return (
                <h2 key={i} className="mt-10 text-[24px] font-bold leading-snug text-secondary sm:text-[28px]">
                    {block.text}
                </h2>
            );
        case 'p':
            // First-party authored content only — safe to render the small inline markup (links/bold).
            return (
                <p
                    key={i}
                    className="mt-5 text-[16px] leading-[1.85] text-[#333] [&_a]:font-semibold [&_a]:text-primary [&_a:hover]:underline"
                    dangerouslySetInnerHTML={{ __html: block.html }}
                />
            );
        case 'ul':
            return (
                <ul key={i} className="mt-5 flex flex-col gap-2.5">
                    {block.items.map((item, j) => (
                        <li key={j} className="flex gap-3 text-[16px] leading-[1.7] text-[#333]">
                            <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            );
        case 'quote':
            return (
                <figure key={i} className="mt-8 border-l-2 border-primary pl-5">
                    <blockquote className="text-[18px] font-medium italic leading-relaxed text-secondary">
                        “{block.text}”
                    </blockquote>
                    <figcaption className="mt-2 text-[13px] text-[#747474]">— {block.cite}</figcaption>
                </figure>
            );
    }
}

export default function BlogPost() {
    const { slug } = useParams();
    const post = slug ? getPost(slug) : undefined;

    // A slug that does not resolve renders the real 404 instead of an empty shell.
    // Split so the view's hooks (useSeo) are never called conditionally.
    if (!post) return <NotFound />;
    return <BlogPostView post={post} />;
}

function BlogPostView({ post }: { post: Post }) {
    const url = `${SITE}/blog/${post.slug}`;
    const dateLabel = new Date(post.date).toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    useSeo({
        title: `${post.title} | Podus`,
        description: post.description,
        canonical: url,
        type: 'article',
        publishedTime: post.date,
        jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            'headline': post.title,
            'description': post.description,
            'datePublished': post.date,
            'dateModified': post.date,
            'inLanguage': 'en-NG',
            'keywords': post.tags.join(', '),
            'mainEntityOfPage': url,
            'author': { '@type': 'Organization', 'name': 'Podus', 'url': SITE },
            'publisher': {
                '@type': 'Organization',
                'name': "Citizens' Gavel",
                'url': SITE,
                'logo': {
                    '@type': 'ImageObject',
                    'url': `${SITE}/android-chrome-512x512.png`,
                    'width': 512,
                    'height': 512,
                },
            },
        },
    });

    return (
        <div className="page-scroll-root bg-white">
            <LandingPagesNavbar />

            <main className="mx-auto max-w-[760px] px-4 pb-24 pt-10 lg:pt-14">
                {/* Breadcrumb */}
                <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-x-2 text-[13px] text-[#9a9a9a]">
                    <Link to="/" className="transition-colors hover:text-primary">
                        Home
                    </Link>
                    <span>/</span>
                    <Link to="/blog" className="transition-colors hover:text-primary">
                        Blog
                    </Link>
                </nav>

                <article className="mt-6">
                    <header>
                        <div className="flex flex-wrap gap-2">
                            {post.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>

                        <h1 className="mt-5 text-[clamp(1.9rem,4.5vw,3rem)] font-bold leading-[1.12] text-secondary">
                            {post.title}
                        </h1>

                        <div className="mt-5 flex flex-wrap items-center gap-2 text-[13px] text-[#747474]">
                            <time dateTime={post.date}>{dateLabel}</time>
                            <span>·</span>
                            <span>{post.readingTime}</span>
                            <span>·</span>
                            <span>By the Podus Team</span>
                        </div>
                    </header>

                    <div className="mt-8">{post.content.map(renderBlock)}</div>

                    {/* CTA back into the product */}
                    <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl bg-[#fafafa] p-6 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-[15px] font-semibold text-secondary">Need legal help? Start with Podus.</p>
                        <a
                            href="https://podus.org"
                            className="inline-flex items-center gap-2 rounded-full bg-secondary px-7 py-3 text-[14px] font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
                        >
                            Get help now <span aria-hidden="true">&rarr;</span>
                        </a>
                    </div>

                    <div className="mt-10">
                        <Link
                            to="/blog"
                            className="inline-flex items-center gap-2 text-[14px] font-semibold text-secondary transition-colors hover:text-primary"
                        >
                            <span aria-hidden="true">&larr;</span> All articles
                        </Link>
                    </div>
                </article>
            </main>

            <LandingPagesFooter />
        </div>
    );
}
